import { fetchApi } from "~/lib/api";
import type {
  AdminGetNotificationSubscribersParams,
  AdminGetNotificationsParams,
  AdminNotificationWithUser,
  AdminSendBroadcastNotificationRequest,
  AdminSendBroadcastNotificationResponse,
  CustomerGetNotificationsParams,
  MarkAllNotificationsReadResponse,
  Notification,
  NotificationSubscriber,
  NotificationsUnreadCountResponse,
  Pagination,
  RegisterDeviceRequest,
  SendPushNotificationRequest,
  SendPushNotificationResponse,
  UnregisterDeviceRequest,
} from "~/types";
import { serializeFilterParams } from "./utils";

export const customerNotificationsService = {
  /**
   * Get all notifications for the current user
   */
  async getNotifications(
    params: CustomerGetNotificationsParams = {},
  ): Promise<Pagination<Notification>> {
    const query = serializeFilterParams(params);
    const endpoint = `/customer/notifications${query}`;
    return fetchApi<Pagination<Notification>>(endpoint);
  },

  /**
   * Get unread notifications count
   */
  async getUnreadCount(): Promise<NotificationsUnreadCountResponse> {
    return fetchApi<NotificationsUnreadCountResponse>(
      "/customer/notifications/unread-count",
    );
  },

  /**
   * Register a device's FCM token for the current user.
   */
  async registerDevice(
    token: string,
    deviceName?: string,
    deviceType?: string,
  ): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>("/customer/notifications/token", {
      method: "POST",
      body: JSON.stringify({
        token,
        deviceName,
        deviceType,
      } satisfies RegisterDeviceRequest),
    });
  },

  /**
   * Unregister a specific device's FCM token.
   */
  async removeDevice(token: string): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>("/customer/notifications/token", {
      method: "DELETE",
      body: JSON.stringify({ token } satisfies UnregisterDeviceRequest),
    });
  },

  /**
   * Mark a single notification as read
   */
  async markAsRead(notificationId: string): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>(
      `/customer/notifications/${notificationId}/read`,
      {
        method: "PATCH",
      },
    );
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<MarkAllNotificationsReadResponse> {
    return fetchApi<MarkAllNotificationsReadResponse>(
      "/customer/notifications/mark-all-read",
      {
        method: "POST",
      },
    );
  },

  /**
   * Delete a single notification
   */
  async deleteNotification(
    notificationId: string,
  ): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>(
      `/customer/notifications/${notificationId}`,
      {
        method: "DELETE",
      },
    );
  },
};

export const adminNotificationsService = {
  /**
   * Send a push notification to a specific user
   */
  async sendToUser(
    data: SendPushNotificationRequest,
  ): Promise<SendPushNotificationResponse> {
    return fetchApi<SendPushNotificationResponse>("/admin/notifications/send", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Broadcast a notification to all users
   */
  async broadcast(
    data: AdminSendBroadcastNotificationRequest,
  ): Promise<AdminSendBroadcastNotificationResponse> {
    return fetchApi<AdminSendBroadcastNotificationResponse>(
      "/admin/notifications/broadcast",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
  },

  /**
   * Get all recent notifications (admin overview)
   */
  async getAll(
    params: AdminGetNotificationsParams = {},
  ): Promise<Pagination<AdminNotificationWithUser>> {
    const query = serializeFilterParams(params);
    const endpoint = `/admin/notifications${query}`;
    return fetchApi<Pagination<AdminNotificationWithUser>>(endpoint);
  },

  /**
   * Get users with active push notification subscriptions (FCM tokens)
   */
  async getSubscribers(
    params: AdminGetNotificationSubscribersParams = {},
  ): Promise<Pagination<NotificationSubscriber>> {
    const query = serializeFilterParams(params);
    const endpoint = `/admin/notifications/subscribers${query}`;
    return fetchApi<Pagination<NotificationSubscriber>>(endpoint);
  },

  /**
   * Delete all notifications
   */
  async deleteAll(): Promise<{ success: boolean }> {
    return fetchApi<{ success: boolean }>('/admin/notifications', {
      method: 'DELETE',
    });
  },
};
